'use client';

import Image from 'next/image';
import Link from 'next/link';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { HomepageSlide, defaultHomepageConfig } from '@/lib/homepage-config';

export default function HeroSlider({
  slides = defaultHomepageConfig.heroSlides,
}: {
  slides?: HomepageSlide[];
}) {
  const activeSlides = useMemo(
    () => slides.filter((slide) => slide.enabled).sort((a, b) => a.sort_order - b.sort_order),
    [slides]
  );
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isInView, setIsInView] = useState(true);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [pausedByUser, setPausedByUser] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setCurrentSlide(0);
  }, [activeSlides.length]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0.12 }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (
      activeSlides.length < 2 ||
      !isInView ||
      reducedMotion ||
      pausedByUser
    ) {
      return;
    }

    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        setCurrentSlide((previous) => (previous + 1) % activeSlides.length);
      }
    }, 6500);

    return () => window.clearInterval(timer);
  }, [activeSlides.length, isInView, pausedByUser, reducedMotion]);

  if (!activeSlides.length) return null;

  const current = activeSlides[currentSlide] || activeSlides[0];
  const primaryHref = current.href && current.href !== '#' ? current.href : '/xay-dung-cau-hinh';
  const slideTitle = current.title || 'PC hiệu năng cao, tối ưu cho bạn';
  const slideSubtitle =
    current.subtitle || 'Linh kiện chính hãng, cấu hình cân bằng và hỗ trợ kỹ thuật tận tâm.';
  const hasMultipleSlides = activeSlides.length > 1;

  const goToPrevious = () =>
    setCurrentSlide((previous) => (previous - 1 + activeSlides.length) % activeSlides.length);
  const goToNext = () =>
    setCurrentSlide((previous) => (previous + 1) % activeSlides.length);

  return (
    <section
      ref={sectionRef}
      className="store-hero"
      aria-roledescription="carousel"
      aria-label="Ưu đãi nổi bật"
    >
      <div className="store-container">
        <div className="relative isolate min-h-[430px] overflow-hidden rounded-[24px] bg-[#090909] text-white shadow-[0_30px_80px_rgba(0,0,0,0.2)] sm:min-h-[470px] lg:min-h-[520px]">
          {activeSlides.map((slide, index) => {
            const active = index === currentSlide;
            return (
              <div
                key={`${slide.title}-${slide.sort_order}-${index}`}
                aria-hidden={!active}
                className={`hero-slide-layer absolute inset-0 transition-opacity duration-700 ${
                  active ? 'z-0 opacity-100' : 'pointer-events-none -z-10 opacity-0'
                }`}
              >
                {slide.img ? (
                  <Image
                    src={slide.img}
                    alt=""
                    fill
                    priority={index === 0}
                    quality={92}
                    sizes="(max-width: 1024px) 100vw, 1500px"
                    className="object-cover object-center lg:object-[66%_center]"
                  />
                ) : (
                  <Image
                    src="/assets/brand/dntech-performance-hero.png"
                    alt=""
                    fill
                    priority={index === 0}
                    sizes="(max-width: 1024px) 100vw, 1500px"
                    className="object-cover object-[66%_center]"
                  />
                )}
              </div>
            );
          })}

          <div className="absolute inset-0 z-[1] bg-[linear-gradient(90deg,rgba(6,6,6,0.98)_0%,rgba(6,6,6,0.9)_35%,rgba(6,6,6,0.46)_67%,rgba(6,6,6,0.2)_100%)]" />
          <div className="absolute inset-x-0 bottom-0 z-[1] h-32 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute -left-20 top-10 z-[1] h-52 w-52 rounded-full bg-[#ffd400]/10 blur-3xl" aria-hidden="true" />

          <div className="relative z-10 flex min-h-[430px] max-w-3xl flex-col justify-center px-6 py-12 sm:min-h-[470px] sm:px-10 lg:min-h-[520px] lg:px-14">
            <div className="mb-5 flex items-center gap-3 text-[11px] font-black uppercase tracking-[0.22em] text-[#ffd400]">
              <span className="h-px w-9 bg-[#ffd400]" aria-hidden="true" />
              Thiết bị & linh kiện hiệu năng cao
            </div>
            <div aria-live="polite" aria-atomic="true">
              <h2 className="hero-caption-motion max-w-2xl text-3xl font-black leading-[1.06] tracking-[-0.045em] text-white sm:text-4xl lg:text-[54px]">
                {slideTitle}
              </h2>
              <p className="hero-caption-motion mt-5 max-w-xl text-sm leading-7 text-neutral-200 sm:text-base lg:text-lg">
                {slideSubtitle}
              </p>
            </div>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Link
                href={primaryHref}
                tabIndex={0}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-[#ffd400] px-6 text-sm font-black text-black transition hover:bg-[#ffe04d]"
              >
                {current.href && current.href !== '#' ? 'Khám phá ngay' : 'Tự build cấu hình'}
                <span aria-hidden="true">→</span>
              </Link>
              <Link
                href="/tim-kiem?q=pc%20gaming"
                className="inline-flex min-h-12 items-center justify-center rounded-xl border border-white/25 bg-black/20 px-6 text-sm font-bold text-white backdrop-blur-sm transition hover:border-[#ffd400]/70 hover:bg-black/35"
              >
                Xem sản phẩm
              </Link>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-semibold text-neutral-300">
              <span className="inline-flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#ffd400]" /> Chính hãng
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#ffd400]" /> Kiểm tra tương thích
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#ffd400]" /> Hỗ trợ kỹ thuật
              </span>
            </div>
          </div>

          {hasMultipleSlides ? (
            <>
              <div className="absolute bottom-4 right-4 z-20 flex items-center gap-1 rounded-full border border-white/15 bg-black/45 p-1 backdrop-blur-md sm:bottom-6 sm:right-6">
                <button
                  type="button"
                  onClick={goToPrevious}
                  className="flex h-11 w-11 items-center justify-center rounded-full text-white transition hover:bg-white/10"
                  aria-label="Xem banner trước"
                >
                  <span aria-hidden="true">←</span>
                </button>
                <span className="min-w-14 text-center text-xs font-bold tabular-nums text-white">
                  {String(currentSlide + 1).padStart(2, '0')} / {String(activeSlides.length).padStart(2, '0')}
                </span>
                <button
                  type="button"
                  onClick={goToNext}
                  className="flex h-11 w-11 items-center justify-center rounded-full text-white transition hover:bg-white/10"
                  aria-label="Xem banner tiếp theo"
                >
                  <span aria-hidden="true">→</span>
                </button>
                {!reducedMotion ? (
                  <button
                    type="button"
                    onClick={() => setPausedByUser((paused) => !paused)}
                    className="flex h-11 w-11 items-center justify-center rounded-full text-white transition hover:bg-white/10"
                    aria-label={pausedByUser ? 'Tiếp tục tự chuyển banner' : 'Tạm dừng tự chuyển banner'}
                    aria-pressed={pausedByUser}
                  >
                    <span aria-hidden="true">{pausedByUser ? '▶' : 'Ⅱ'}</span>
                  </button>
                ) : null}
              </div>

              <div className="absolute bottom-5 left-6 z-20 hidden items-center gap-2 sm:flex lg:left-14">
                {activeSlides.map((slide, index) => (
                  <button
                    key={`${slide.title}-indicator-${index}`}
                    type="button"
                    onClick={() => setCurrentSlide(index)}
                    className="flex h-11 items-center"
                    aria-label={`Xem banner ${index + 1}`}
                    aria-current={index === currentSlide ? 'true' : undefined}
                  >
                    <span
                      className={`block h-1 rounded-full transition-all ${
                        index === currentSlide ? 'w-10 bg-[#ffd400]' : 'w-5 bg-white/40'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </section>
  );
}
